import { cn } from "../../libs/utils"
import { BrandMark } from "./brand-mark"

interface PageLoaderProps {
  /**
   * Height utility for the container. Callers set this per context — the
   * top-level Suspense fallback uses `min-h-screen`; layouts pass `h-full`
   * to fill just the content area and keep the shell (sidebar/top bar) visible.
   */
  className?: string
}

/**
 * Theme-aware route/Suspense loading state: the GRIDBOX brand mark beating
 * like a heart, centered on the app background. The pulse replaces the generic
 * spinner so waiting reads as the product rather than a loading widget. It
 * animates `transform` only, which is safe across theme switches, and stops
 * entirely under `prefers-reduced-motion`.
 */
export function PageLoader({ className }: PageLoaderProps) {
  return (
    <div className={cn("flex w-full flex-col items-center justify-center gap-5 bg-bg", className)}>
      {/* GRIDBOX brand mark, beating */}
      <BrandMark className="size-28 animate-heartbeat object-contain" />
      <span className="sr-only">Loading…</span>
    </div>
  )
}

/**
 * The same beating GRIDBOX mark for a section or page that is loading, with an
 * optional caption under it (e.g. "Loading group…"). Use instead of a spinner
 * for any whole-area loading state; keep the small spinner for buttons.
 */
export function BrandLoader({
  label,
  size = "md",
  className,
}: {
  label?: string
  /** md: sections and pages; sm: cards and compact panels. */
  size?: "sm" | "md"
  className?: string
}) {
  return (
    <output
      className={cn("flex flex-col items-center justify-center gap-3", className)}
      aria-live="polite"
    >
      <BrandMark
        className={cn("animate-heartbeat object-contain", size === "sm" ? "size-10" : "size-16")}
      />
      {label ? (
        <span className="text-sm font-medium text-content-2">{label}</span>
      ) : (
        <span className="sr-only">Loading…</span>
      )}
    </output>
  )
}
